<i18n>
{
	"en": {
		"noFreightLines": "There are no freight lines yet.",
		"freightLines": "Freight Lines",
		"description": "Description",
		"grossWeight": "Gross Weight",
		"chargableWeight": "Chargable Weight",
		"marks": "Marks",
		"netWeight": "Net Weight",
		"numberOfUnits": "Quantity",
		"numberOfPackages": "Quantity",
		"packageTypeCode": "Code",
		"numberOfPallets": "Pallets",
		"height": "Height",
		"width": "Width",
		"length": "Length",
		"volume": "Volume",
		"loadingMeters": "Loading meters",
		"palletType": "Pallet Type",
		"palletPlacePrice": "P.P. Price",
		"palletPriceFactorPrice": "Pallet Price Factor",
		"palletPriceFactorTruck": "P.P. Car"
	},
	"no": {
		"noFreightLines": "Det er ingen fraktlinjer ennå.",
		"freightLines": "Fraktlinjer",
		"description": "Beskrivelse",
		"grossWeight": "Brutto vekt",
		"chargableWeight": "Fraktberegningsvekt",
		"marks": "Merknad",
		"netWeight": "Netto vekt",
		"numberOfUnits": "Antall",
		"numberOfPackages": "Antall",
		"packageTypeCode": "Kode",
		"numberOfPallets": "Paller",
		"height": "Høyde",
		"width": "Bredde",
		"length": "Lengde",
		"volume": "Volum",
		"loadingMeters": "Lastemeter",
		"palletType": "Palletype",
		"palletPlacePrice": "P.P. Pris",
		"palletPriceFactorPrice": "Pallepris faktor",
		"palletPriceFactorTruck": "P.P. Bil"
	}
}
</i18n>
<template>
	<div ref="freightLines" class="freightlines">
		<div class="freightlines-header">
			<div>
				<div class="freightlines-header-details">

					<template v-for="(value, key) in sumsPrettyPrint">

						<span :key="key">

							<span class="sum-field">{{ $t(key) }}: </span>

							<span class="sum-number">
								<span>{{ value }}</span>
								<span
									v-if="sumsUnits[key].length"
									class="sum-number-unit"
								>{{sumsUnits[key]}}</span>
							</span>
						</span>
					</template>
				</div>
			</div>
			<div>
				<t-icon-button
					v-if="freightLines.length > 1"
					:disabled="!rowsSelected || orderIsLocked"
					icon="minus"
					type="bp"
					appearance="minimal"
					intent="danger"
					style="margin-right: 4px"
					tabindex="-1"
					@click="deleteLines"
				/>
				<t-icon-button
					icon="plus"
					type="bp"
					appearance="minimal"
					tabindex="-1"
					:disabled="orderIsLocked"

					@click="addLine"
				/>
			</div>
		</div>

		<t-grid
			v-if="freightLines && freightLines.length > 0"
			ref="grid"
			:columnDefinitions="columnDefinitions"
			:rowHeight="42"
			:items="freightLines"
			:showFilters="false"
			:sort="[{field: 'lineNo', direction: 'asc'}]"
			:selectableMode="['checkbox']"
			selectable
			disableSelectCheckboxTabbing
			disableContainerFocus
			idKey="lineNo"
			class="freightlines-content"
			@selected="onRowsSelected"
			@column-moved="onColumnMoved($event)"
		>
			<template #lineNo="{ item }">
				<t-pill size="small">{{item.lineNo}}</t-pill>
			</template>
			<template #packageTypeCode="{ item, columnIndex, rowIndex }">
				<PackageTypesFormInput
					:value="item.packageTypeCode"
					noTitle
					:disabled="orderIsLocked"

					@select="onSelectPackageType(item.lineNo, $event)"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #marks="{ item, columnIndex, rowIndex }">
				<t-text-input
					:value="item.marks"
					:disabled="orderIsLocked"

					@change="onStrInput(item.lineNo, 'marks', $event )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #description="{ item, columnIndex, rowIndex }">
				<t-text-input
					:value="item.description"
					:disabled="orderIsLocked"

					@change="onStrInput(item.lineNo, 'description', $event )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #netWeight="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.netWeight,1)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'netWeight', truncateDecimal($event,1) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #grossWeight="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.grossWeight,1)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'grossWeight', truncateDecimal($event,1) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #palletPackageCode="{ item, columnIndex, rowIndex }">
				<PalletPackageTypesFormInput
					:value="item.palletPackageCode"
					noTitle
					:disabled="orderIsLocked"

					@select="onSelectPalletPackageType(item.lineNo, $event)"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #palletPlacePrice="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.palletPlacePrice"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'palletPlacePrice', $event )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #palletPriceFactorPrice="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.palletPriceFactorPrice"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'palletPriceFactorPrice', $event )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #palletPriceFactorTruck="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.palletPriceFactorTruck"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'palletPriceFactorTruck', $event )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #numberOfPallets="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.numberOfPallets"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'numberOfPallets', $event )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #numberOfUnits="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.numberOfUnits"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'numberOfUnits', $event )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #length="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.length, 1)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'length', truncateDecimal($event,1) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #width="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.width, 1)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'width', truncateDecimal($event,1) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #height="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.height,1)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'height', truncateDecimal($event,1) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #volume="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="formatVolume(item.volume)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'volume', formatVolume($event) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #loadingMeters="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.loadingMeters,2)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'loadingMeters', truncateDecimal($event,2) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
			<template #chargableWeight="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.chargableWeight, 1)"
					min="0"
					:disabled="orderIsLocked"

					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'chargableWeight', truncateDecimal($event,1) )"
					@keydown="onKeyDown($event, columnIndex, rowIndex)"
				/>
			</template>
		</t-grid>

		<t-no-content-panel
			v-else
			:text="$t('noFreightLines')"
			icon="order"
			class="freightlines-content"
			noBetweenGap
		/>
	</div>
</template>

<script>
import { goodsTypesIcons, statuses, truncateDecimal } from '@/core';
import { mapMutations, mapGetters } from '../store/newOrder.store';
import PackageTypesFormInput from '@/modules/packageTypes/PackageTypesFormInput';
import PalletPackageTypesFormInput from '@/modules/packageTypes/PalletPackageTypesFormInput';
import { mapOrderSumKeysToFreightLineKey } from './freightLineSumKeys';
import TNumberInput from '@/components/evergreen/timpex/inputs/TNumberInput';

export default {
	name: 'FreightLines',
	components: { TNumberInput, PackageTypesFormInput, PalletPackageTypesFormInput },
	props: { order: Object, events: Object },
	data() {
		return {
			rowsSelected: 0,
			focusNextOnUpdate: false,
			lastTargetBeforeAddingLine: null,
			lineNoColumnIndex: 0
		};
	},
	computed: {
		...mapGetters(['freightLineSums', 'orderIsLocked']),

		freightLines() {
			return this.order.freightLines;
		},

		sumsPrettyPrint() {

			let sumsPrettyPrint = {};

			for (let orderSumKey in mapOrderSumKeysToFreightLineKey) {

				sumsPrettyPrint[orderSumKey] = this.freightLineSums[orderSumKey]
					.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
			}

			return sumsPrettyPrint;
		},

		sumsUnits() {
			return {
				lineNo: '',
				packageTypeCode: '',
				marks: '',
				netWeight: 'kg',
				grossWeight: 'kg',
				numberOfUnits: '',
				numberOfPackages: '',
				numberOfPallets: '',
				length: 'm',
				width: 'm',
				height: 'm',
				volume: 'm³',
				loadingMeters: 'm',
				chargableWeight: 'kg',
			};
		},

		columnDefinitions() {
			return [{
				field: 'lineNo',
				displayName: '#',
				width: 35,
				center: true,
			}, {
				field: 'packageTypeCode',
				displayName: this.$t('packageTypeCode'),
				width: 150,
				resizable: true,
			}, {
				field: 'marks',
				displayName: this.$t('marks'),
				minWidth: 150,
				resizable: true,
			}, {
				field: 'description',
				displayName: this.$t('description'),
				minWidth: 150,
				resizable: true,
			}, {
				field: 'netWeight',
				displayName: this.$t('netWeight'),
				type: 'number',
				width: 80,
				resizable: true,
			}, {
				field: 'grossWeight',
				displayName: this.$t('grossWeight'),
				type: 'number',
				width: 80,
				resizable: true,
			}, {
				field: 'numberOfUnits',
				displayName: this.$t('numberOfUnits'),
				type: 'number',
				width: 60,
			}, {
				field: 'palletPackageCode',
				displayName: this.$t('palletType'),
				width: 150,
				resizable: true,
			}, {
				field: 'palletPlacePrice',
				displayName: this.$t('palletPlacePrice'),
				type: 'number',
				width: 60,
				resizable: true,
			}, {
				field: 'palletPriceFactorPrice',
				displayName: this.$t('palletPriceFactorPrice'),
				type: 'number',
				width: 60,
				resizable: true,
			}, {
				field: 'palletPriceFactorTruck',
				displayName: this.$t('palletPriceFactorTruck'),
				type: 'number',
				width: 60,
				resizable: true,
			}, {
				field: 'numberOfPallets',
				displayName: this.$t('numberOfPallets'),
				type: 'number',
				width: 60,
				resizable: true,
			}, {
				field: 'length',
				displayName: this.$t('length'),
				type: 'number',
				width: 60,
				resizable: true,
			}, {
				field: 'width',
				displayName: this.$t('width'),
				type: 'number',
				width: 60,
				resizable: true,
			}, {
				field: 'height',
				displayName: this.$t('height'),
				type: 'number',
				width: 60,
				resizable: true,
			}, {
				field: 'volume',
				displayName: this.$t('volume'),
				type: 'number',
				width: 70,
				resizable: true,
			}, {
				field: 'loadingMeters',
				displayName: this.$t('loadingMeters'),
				type: 'number',
				width: 80,
				resizable: true,
			}, {
				field: 'chargableWeight',
				displayName: this.$t('chargableWeight'),
				type: 'number',
				width: 130,
				resizable: true,
			}];
		},

	},
	methods: {

		...mapMutations(['setFreightLinesValue', 'addFreightLine', 'deleteFreightLine']),
		truncateDecimal,
		onKeyDown(event, columnIndex, rowIndex) {

			if ( !(event.key === 'Tab' || event.key === 'Enter') )
				return;

			if (
				this.inputCellIsInLastColumnAndLastRow(columnIndex, rowIndex)
				&& !event.shiftKey
			) {
				this.addLine();
				event.stopPropagation();
				event.preventDefault();
			}
		},

		inputCellIsInLastColumnAndLastRow(columnIndex, rowIndex) {

			if (this.lineNoColumnIndex === this.columnDefinitions.length - 1) {

				return columnIndex === this.columnDefinitions.length-2
					&& rowIndex === this.freightLines.length-1;

			} else {
				return columnIndex === this.columnDefinitions.length-1
					&& rowIndex === this.freightLines.length-1;
			}
		},

		onStrInput(lineNo, key, value) {

			this.setFreightLinesValue({ lineNo, key, value });
		},

		onIntInput(lineNo, key, strValue) {

			let intValue = parseInt(strValue);

			if (isNaN(intValue))
				intValue = 0;

			this.setFreightLinesValue({ lineNo, key, value: intValue });
		},

		onFloatInput(lineNo, key, strValue) {

			let floatValue = parseFloat(strValue);

			if (isNaN(floatValue))
				floatValue = 0;

			this.setFreightLinesValue({ lineNo, key, value: floatValue });
		},

		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		getStatusIcon(item) {
			return goodsTypesIcons(item.goodsType);
		},
		getStatusColor(item) {
			return statuses.color(item.status);
		},
		deleteLines() {
			let selectedLines = this.$refs.grid.getSelectedItems();

			for (let line of selectedLines) {
				this.deleteFreightLine( line.lineNo );
			}

			this.$refs.grid.resetSelectedRows();
		},
		addLine() {
			this.addFreightLine({
				lineNo: this.freightLines.length
					? this.freightLines[this.freightLines.length - 1].lineNo + 1
					: 1,
				description: '',
				marks: '',
				netWeight: 0,
				grossWeight: 0,
				numberOfUnits: 0,
				chargableWeight: 0,
				height: 0,
				length: 0,
				loadingMeters: 0,
				palletPackageCode: '',
				palletPackageTypeCode: '',
				palletPlacePrice: 0,
				palletPriceFactorPrice: 0,
				palletPriceFactorTruck: 0,
				numberOfPallets: 0,
				packageTypeCode: '',
				volume: 0,
				width: 0
			});
		},
		onSelectPackageType(lineNo, event) {
			this.setPackageType(lineNo, event);

			window.focusNext();
		},
		setPackageType(lineNo, packageType) {
			if (packageType && packageType.packageCode && packageType.packageName) {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'packageTypeCode', value: packageType.packageCode });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'description', value: packageType.packageName.no });
			} else {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'packageTypeCode', value: '' });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'description', value: '' });
			}
		},
		onSelectPalletPackageType(lineNo, event) {
			this.setPalletPackageType(lineNo, event);

			window.focusNext();
		},
		setPalletPackageType(lineNo, packageType) {
			if (packageType && packageType.packageCode && packageType.packageName) {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageCode', value: packageType.packageCode });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageTypeCode', value: packageType.packageTypeCode });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorPrice', value: packageType.palletPriceFactorPrice });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorTruck', value: packageType.palletPriceFactorTruck });
			} else {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageCode', value: '' });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageTypeCode', value: '' });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorPrice', value: 0 });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorTruck', value: 0 });
			}
		},

		onColumnMoved(event) {

			if (event.name === 'lineNo')
				this.lineNoColumnIndex = event.newIndex;
		},
		formatVolume(num) {
			return Number.parseFloat(num.toString()).toFixed(3);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.freightlines {
	height: 100%;
	width:100%;
	min-height: inherit;
}

.sum-field {
	@extend %font-families-ui;
	color: get-color($text, 'default');
	font-weight: 400;
	margin-right: 2px;
}
.sum-number {
	@extend %font-families-ui;
	margin-right: 32px;
	font-weight: 500;
}

.sum-number-unit {
	@extend %font-families-ui;
	margin-left: 3px;
	font-weight: 400;
}

.freightlines-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	background: white;

	.freightlines-header-details {
		margin-left: 28px;
	}

	div {
		display: flex;
		justify-content: space-between;
		align-items: center;
		background: white;
	}
}

.freightlines-content {
	height: calc(100% - 60px);
	min-height: inherit;
}
</style>
